/**
 * Small pure formatters shared by the console pages.
 *
 * Kept intentionally thin: every console page renders currency and
 * token counts identically, so centralising the locale + options here
 * avoids drift.
 */

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const TOKEN_FORMATTER = new Intl.NumberFormat('en-US');
const CNY_VALUE_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const CNY_MICRO_VALUE_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 6,
  maximumFractionDigits: 6,
});

const SERVER_UTC_TIMESTAMP_RE = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?)$/;

export function parseTimestampMs(value: string): number {
  const trimmed = value.trim();
  const match = SERVER_UTC_TIMESTAMP_RE.exec(trimmed);
  if (match) {
    const [, date, time] = match;
    const normalized = `${date}T${time}`.replace(/\.(\d{3})\d+$/, '.$1');
    return Date.parse(`${normalized}Z`);
  }
  return Date.parse(trimmed);
}

function isValidRate(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function formatCnyMagnitude(cents: number): string {
  return `¥${CNY_VALUE_FORMATTER.format(Math.abs(cents) / 100)}`;
}

function formatCnyMicroMagnitude(micros: number): string {
  return `¥${CNY_MICRO_VALUE_FORMATTER.format(Math.abs(micros) / 1_000_000)}`;
}

/**
 * Format a USD cents value (always-positive display). Pass `signed=true` to
 * render charges as "-$0.12" and credits as "+$5.00". Input is integer
 * cents (the wire format since PR-B.5).
 */
export function formatUsdCents(cents: number, signed = false): string {
  if (!Number.isFinite(cents)) {
    return '—';
  }
  const dollars = cents / 100;
  if (!signed) {
    return USD_FORMATTER.format(Math.abs(dollars));
  }
  const base = USD_FORMATTER.format(Math.abs(dollars));
  if (cents > 0) {
    return `+${base}`;
  }
  if (cents < 0) {
    return `-${base}`;
  }
  return base;
}

/**
 * Format integer RMB cents. This is the tenant-facing primary money display;
 * USD credit remains the ledger unit underneath.
 */
export function formatCnyCents(cents: number, signed = false): string {
  if (!Number.isFinite(cents)) {
    return '—';
  }
  const base = formatCnyMagnitude(cents);
  if (!signed || cents === 0) {
    return base;
  }
  return cents > 0 ? `+${base}` : `-${base}`;
}

/**
 * Convert USD ledger cents into RMB display cents using the configured policy
 * rate. Invalid or missing rates return null so pages render an explicit empty
 * state instead of silently falling back to a hardcoded rate.
 */
export function usdCentsToCnyCents(
  usdCents: number,
  cnyPerUsd: number | null | undefined,
): number | null {
  if (!Number.isFinite(usdCents) || !isValidRate(cnyPerUsd)) {
    return null;
  }
  return Math.round(usdCents * cnyPerUsd);
}

export function usdMicrosToCnyCents(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
): number | null {
  if (!Number.isFinite(usdMicros) || !isValidRate(cnyPerUsd)) {
    return null;
  }
  return Math.round((usdMicros * cnyPerUsd) / 10_000);
}

export function usdMicrosToCnyMicros(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
): number | null {
  if (!Number.isFinite(usdMicros) || !isValidRate(cnyPerUsd)) {
    return null;
  }
  return Math.round(usdMicros * cnyPerUsd);
}

export function usdMicrosFromCentsFallback(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
): number {
  if (typeof usdMicros === 'number' && Number.isFinite(usdMicros) && usdMicros !== 0) {
    return Math.trunc(usdMicros);
  }
  return Math.trunc(usdCents ?? 0) * 10_000;
}

export function formatUsdCentsAsCny(
  usdCents: number,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  const cnyCents = usdCentsToCnyCents(usdCents, cnyPerUsd);
  return cnyCents === null ? '—' : formatCnyCents(cnyCents, signed);
}

export function formatUsdMicrosAsCny(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  const cnyMicros = usdMicrosToCnyMicros(usdMicros, cnyPerUsd);
  if (cnyMicros === null) {
    return '—';
  }
  const base = formatCnyMicroMagnitude(cnyMicros);
  if (!signed || cnyMicros === 0) {
    return base;
  }
  return cnyMicros > 0 ? `+${base}` : `-${base}`;
}

/**
 * Primary money display for balances, KPI cards, funding capacity, and face
 * values. This intentionally rounds metered USD micros to CNY cents.
 */
export function formatUsdMicrosAsCnyDisplay(
  usdMicros: number,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  const cnyCents = usdMicrosToCnyCents(usdMicros, cnyPerUsd);
  return cnyCents === null ? '—' : formatCnyCents(cnyCents, signed);
}

export function formatUsdMicrosOrCentsAsCnyDisplay(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  return formatUsdMicrosAsCnyDisplay(
    usdMicrosFromCentsFallback(usdMicros, usdCents),
    cnyPerUsd,
    signed,
  );
}

/**
 * Metered spend display for request rows, usage drilldowns, and billing
 * diagnostics where sub-cent precision is part of the evidence.
 */
export function formatUsdMicrosOrCentsAsCnyMetered(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  return formatUsdMicrosAsCny(usdMicrosFromCentsFallback(usdMicros, usdCents), cnyPerUsd, signed);
}

/**
 * Back-compat alias for older imports. New UI code should choose Display or
 * Metered explicitly.
 */
export function formatUsdMicrosOrCentsAsCny(
  usdMicros: number | null | undefined,
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
  signed = false,
): string {
  return formatUsdMicrosOrCentsAsCnyMetered(usdMicros, usdCents, cnyPerUsd, signed);
}

/**
 * Format decimal USD rates such as model pricing in USD / MTok.
 * These are not cents and must not go through `formatUsdCents`.
 */
export function formatUsdRate(value: number): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  return USD_FORMATTER.format(value);
}

/**
 * Format decimal USD rates, such as model pricing in USD / MTok, as the RMB
 * display rate at the configured policy rate.
 */
export function formatCnyRateFromUsdRate(
  usdRate: number | null | undefined,
  cnyPerUsd: number | null | undefined,
): string {
  if (typeof usdRate !== 'number' || !Number.isFinite(usdRate) || !isValidRate(cnyPerUsd)) {
    return '—';
  }
  return `¥${CNY_VALUE_FORMATTER.format(Math.abs(usdRate * cnyPerUsd))}`;
}

export function formatDisplayRateHint(cnyPerUsd: number | null | undefined): string {
  if (!isValidRate(cnyPerUsd)) {
    return '—';
  }
  return `1 USD = ¥${cnyPerUsd.toFixed(2)}`;
}

/**
 * Render the server-provided decimal-string USD value with the `$` prefix.
 * Server sends strings like `"5.00"` or `"-1.25"`; we pass them through
 * verbatim so trailing zeros survive the JSON round-trip.
 */
export function formatUsdString(raw: string, signed = false): string {
  if (typeof raw !== 'string' || raw.length === 0) {
    return '—';
  }
  const negative = raw.startsWith('-');
  const magnitude = negative ? raw.slice(1) : raw;
  const formatted = `$${magnitude}`;
  if (!signed) return formatted;
  if (negative) return `-${formatted}`;
  if (magnitude === '0.00') return formatted;
  return `+${formatted}`;
}

export function formatTokens(count: number): string {
  if (!Number.isFinite(count) || count < 0) {
    return '—';
  }
  return TOKEN_FORMATTER.format(Math.floor(count));
}

/**
 * #1670: unit-metered request rows carry structurally-zero token counters —
 * the request was billed per unit, not per token. Rendering `0` there reads
 * as "free tokens"; the em dash says "not a token request" instead. Token
 * rows (`unitBilling` absent) get the pre-formatted value untouched, so
 * every legacy rendering stays byte-identical. The ONE shared discriminator
 * for every per-request token display (Requests table, member request
 * panels) — do not re-derive it per page.
 */
export function unitAwareTokenCell(
  row: { readonly unitBilling?: unknown },
  formatted: string,
): string {
  return row.unitBilling === undefined ? formatted : '—';
}

/**
 * Render an ISO-8601 timestamp in the browser's local time.
 * Falls back to the raw string on parse failure.
 */
export function formatTimestamp(iso: string | null | undefined): string {
  if (typeof iso !== 'string' || iso.length === 0) {
    return '—';
  }
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) {
    return iso;
  }
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Whole days from now until an expiry, floored, or `null` when the input
 * is not a readable timestamp.
 *
 * Floored, not rounded: a lot with 6.9 days left reads as "6 days", which
 * tells the tenant to act a day sooner than reality rather than a day
 * later. Negative values are possible and meaningful — the gift-expiry
 * sweep runs hourly, so a lot can sit expired-but-not-yet-clawed-back for
 * up to an hour; callers render that as "expired", not as a countdown.
 */
export function daysUntil(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (typeof iso !== 'string' || iso.length === 0) {
    return null;
  }
  const ms = parseTimestampMs(iso);
  if (!Number.isFinite(ms)) {
    return null;
  }
  return Math.floor((ms - now.getTime()) / 86_400_000);
}

/**
 * Back-compat alias for `formatUsdCents`. Keeps call sites that still
 * reference `formatUsd` compiling after PR-B.5's cents migration. New code
 * should prefer `formatUsdCents` or `formatUsdString` directly.
 */
export const formatUsd = formatUsdCents;
